<template>
  <b-modal
    :id="modalId"
    title="Changes"
    size="xl"
    no-close-on-backdrop
    hide-footer
    static
    lazy
  >
    <template #modal-header-close>
      <i class="fa fa-close" />
    </template>
    <b-row>
      <b-col>
        <b-table
          class="mb-0 version-changes-table"
          :items="changes"
          :fields="columns"
          show-empty
          responsive
          table
        >
          <template #cell(field)="data">
            <code>{{ data.item.field }}</code>
          </template>
          <template #cell(before)="data">
            <span
              class="version-change-cell"
              :class="beforeCellClass(data.item.before)"
            >
              <template v-if="isEmpty(data.item.before)">
                —
              </template>
              <b-link
                v-else-if="linkFor(data.item.field, data.item.before)"
                :to="linkFor(data.item.field, data.item.before)"
                :title="tooltipFor(data.item.field, data.item.before)"
                v-b-tooltip.hover.window
              >
                {{ displayFor(data.item.field, data.item.before) }}
              </b-link>
              <code v-else>
                {{ displayFor(data.item.field, data.item.before) }}
              </code>
            </span>
          </template>
          <template #cell(after)="data">
            <span
              class="version-change-cell"
              :class="afterCellClass(data.item.before, data.item.after)"
            >
              <template v-if="isEmpty(data.item.after)">
                —
              </template>
              <b-link
                v-else-if="linkFor(data.item.field, data.item.after)"
                :to="linkFor(data.item.field, data.item.after)"
                :title="tooltipFor(data.item.field, data.item.after)"
                v-b-tooltip.hover.window
              >
                {{ displayFor(data.item.field, data.item.after) }}
              </b-link>
              <code v-else>
                {{ displayFor(data.item.field, data.item.after) }}
              </code>
            </span>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <hr class="mt-4">

    <b-row>
      <b-col
        cols="3"
        offset="9"
      >
        <b-button
          variant="primary"
          block
          id="version-changes-close-button"
          @click="closeModal"
        >
          Close
        </b-button>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { map as _map } from 'lodash';

export default {
  name: 'VersionChangesModal',
  props: {
    value: Object,
  },
  methods: {
    isEmpty(value) {
      return value === null || value === undefined || value === '';
    },
    beforeCellClass(value) {
      if (this.isEmpty(value)) {
        return 'version-change-cell--empty';
      }
      return 'version-change-cell--before';
    },
    afterCellClass(beforeValue, afterValue) {
      if (this.isEmpty(afterValue)) {
        return 'version-change-cell--empty';
      }
      if (!this.isEmpty(beforeValue)) {
        return 'version-change-cell--after version-change-cell--with-arrow';
      }
      return 'version-change-cell--after';
    },
    linkFor(field, value) {
      if (!value || typeof value !== 'object') {return null;}
      switch (field) {
        case 'Assigned To':
        case 'Done By':
        case 'User':
        case 'Manager':
          return value.id ? { name: 'user', params: { id: value.id } } : null;
        case 'Client':
          return value.id ? { name: 'client', params: { id: value.id } } : null;
        case 'Location':
          return value.id ? { name: 'location', params: { id: value.id } } : null;
        case 'Invoice':
          return value.id ? { name: 'invoice', params: { id: value.id } } : null;
        case 'Service':
          return value.id ? { name: 'service', params: { id: value.id } } : null;
        case 'Parent':
          return value.id ? { name: 'work-order', params: { id: value.id } } : null;
        case 'Technician Payment':
          return value.id ? { name: 'technician-payment', params: { id: value.id } } : null;
        default:
          return null;
      }
    },
    displayFor(field, value) {
      if (!value || typeof value !== 'object') {return value;}
      switch (field) {
        case 'Assigned To':
        case 'Done By':
        case 'User':
        case 'Manager':
          return [value.first_name, value.last_name].filter(Boolean).join(' ');
        case 'Client':
        case 'GL Code':
        case 'Service':
        case 'Organization':
          return value.name;
        case 'Location':
          return value.store_number;
        case 'Invoice':
        case 'Parent':
        case 'Technician Payment':
          return value.number;
        case 'Work Order Charge':
          return value.description;
        default:
          return value;
      }
    },
    tooltipFor(field, value) {
      if (!value || typeof value !== 'object') {return '';}
      if (field === 'Location') {return this.getLocationTitle(value);}
      return '';
    },
    getLocationTitle(location) {
      return `${location.street}, ${location.city}, ${location.state}, ${location.zip_code}`;
    },
    closeModal() {
      this.$bvModal.hide(this.modalId);
    },
  },
  data() {
    return {
      columns: ['field', 'before', 'after'],
    };
  },
  computed: {
    modalId() {
      return `version-changes-${this.value.id}-modal`;
    },
    changes() {
      return _map(this.value.changes, (change, field) => {
        return { field: field, before: change[0], after: change[1] };
      });
    },
  },
};
</script>

<style scoped>
.version-change-cell--before {
  color: #c92a2a;
  text-decoration: line-through;
}

.version-change-cell--before code,
.version-change-cell--before a {
  color: inherit;
  text-decoration: line-through;
}

.version-change-cell--after {
  color: #2b8a3e;
}

.version-change-cell--after code,
.version-change-cell--after a {
  color: inherit;
}

.version-change-cell--with-arrow::before {
  content: '→';
  margin-right: 0.5rem;
  color: #adb5bd;
}

.version-change-cell--empty {
  color: #adb5bd;
  font-style: italic;
}

a {
  color: #e83e8c;
  font-size: 0.95rem;
}
</style>
